import type { SolUiContext } from "@/modern/components/chatbox/types";

import type {
  MappedNotificationCounts,
  NotificationSummaryModel,
} from "../types";

/** Exact page_intent from the Sol notify Ask contract. */
export const UPDATES_PAGE_INTENT =
  "The user is in the Updates inbox asking about the notifications currently summarized on this screen. Prefer notify counts and inbox tools. Use scope=all unless they say my notifications. Use category=schedule for schedule notifications. Notable or unread means needs attention. Failed async jobs are failures. Completed background work is inbox async_status=Completed, not the in-flight jobs tray.";

export const UPDATES_SCREEN_LABEL = "Updates";

/** UI category keys that must be remapped before sending to Sol. */
const UI_TO_SOL_CATEGORY: Record<string, string> = {
  imports: "import",
  files: "file",
  "board-alert": "board_alert",
};

const FORBIDDEN_SOL_ALIASES = new Set(["imports", "files", "board-alert"]);

/**
 * Map a frontend notification category key to the Sol server key.
 * Returns null when the key is a forbidden UI alias that failed to remap.
 */
export function toSolCategoryKey(uiCategory: string): string | null {
  const mapped = UI_TO_SOL_CATEGORY[uiCategory] ?? uiCategory;
  if (FORBIDDEN_SOL_ALIASES.has(mapped)) return null;
  return mapped;
}

export interface UpdatesAskPeriod {
  period_label: string;
  period_start: string;
}

/**
 * Build a fresh Updates Ask `ui_context` from the summary already on screen.
 * Omit period fields unless a real oldest-alert window is supplied.
 */
export function buildUpdatesAskUiContext(args: {
  summary: Pick<NotificationSummaryModel, "total" | "unread" | "notable">;
  mappedCounts: Pick<MappedNotificationCounts, "categories">;
  period?: UpdatesAskPeriod;
}): SolUiContext {
  const category_counts = args.mappedCounts.categories
    .filter((c) => c.count > 0)
    .map((c) => {
      const category = toSolCategoryKey(c.value);
      if (!category) return null;
      return { category, count: c.count };
    })
    .filter((entry): entry is { category: string; count: number } => entry != null);

  return {
    screen_label: UPDATES_SCREEN_LABEL,
    page_intent: UPDATES_PAGE_INTENT,
    visible_snapshot: {
      total: args.summary.total,
      unread: args.summary.unread,
      notable: args.summary.notable,
      ...(args.period
        ? {
            period_label: args.period.period_label,
            period_start: args.period.period_start,
          }
        : {}),
      category_counts,
    },
  };
}
